import { memo } from 'react';
import styled, { type CSSObject } from '@emotion/styled';

import { colorAlpha } from '../../../lib/colorAlpha';
import getEventDateTime, {
  type EventDateTimeParams,
} from '../../../lib/getEventDateTime';
import { themeFont } from '../../../lib/themeFont';

import TimeRange from './TimeRange';

import { css, type ThemeProps } from '@emotion/react';
import type { Gds_SubEvent } from '../../../types/graphql';
import { variables } from '@xo-union/tk-ui-breakpoints';

// A cover photo shrinks the single page view column to 40% of the viewport, so
// it stays too narrow for the two column layout until the viewport reaches
// 1920px (768 / 0.4). SinglePageContent marks that column with .narrowColumn.
const inNarrowColumn = '(min-width: 1025px) and (max-width: 1919.98px)';

const CompactSubEvent = () => css`
  gap: 16px;
  flex-wrap: wrap;
  padding: 28px 0;
`;

const DesktopSubEvent = () => css`
  padding: 48px 16px;
  gap: 32px;

  .verticalLayout.homePage & {
    padding: 28px 0;
    margin-bottom: 16px;
    flex-direction: column;
  }
`;

const SubEvent = styled('div')`
  display: flex;
  max-width: 720px;

  @media (${variables.inMobile}) {
    ${CompactSubEvent}
  }

  @media (${variables.inDesktop}) {
    ${DesktopSubEvent}
  }

  @media ${inNarrowColumn} {
    .narrowColumn & {
      ${CompactSubEvent}
    }
  }

  border-bottom: 1px solid
    ${(props: ThemeProps): string =>
      colorAlpha(props.theme.fonts.s6.color || '', '15%')};

  ${(props: ThemeProps): CSSObject | undefined =>
    props.theme.components.SubEvent}
`;

interface BlockProps {
  hasTime: boolean;
}

const MobileBlock = () => css`
  overflow-wrap: break-word;
  width: 100%;
`;

const DesktopBlock = (props: BlockProps) => css`
  .verticalLayout.homePage & {
    ${!props.hasTime ? 'text-align: unset;' : ''}
  }
`;

const Block = styled('div')<BlockProps>`
  ${(props: BlockProps): string =>
    !props.hasTime ? 'text-align: center;' : ''}

  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (${variables.inMobile}) {
    ${MobileBlock}
  }

  @media (${variables.inDesktop}) {
    ${DesktopBlock}
  }

  @media ${inNarrowColumn} {
    .narrowColumn & {
      ${MobileBlock}
    }
  }
`;

const TimeBlock = styled(Block)`
  @media (${variables.inDesktop}) {
    min-width: 40%;
    width: 40%;
    text-align: end;

    .verticalLayout.homePage & {
      text-align: left;
      width: 100%;
    }
  }

  @media ${inNarrowColumn} {
    .narrowColumn & {
      min-width: 100%;
      width: 100%;
      text-align: center;
    }
  }
`;

const Name = styled('h4')`
  line-height: 1;

  ${themeFont('s4')}
`;

const Detail = styled('div')`
  ${themeFont('s6')}
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Notes = styled('div')`
  margin: 8px 0 0;
  white-space: pre-line;

  ${themeFont('s6')}
`;

const StyledTimeRange = styled(TimeRange)`
  @media (${variables.inMobile}) {
    ${themeFont('s5')}
  }

  @media ${inNarrowColumn} {
    .narrowColumn & {
      ${themeFont('s5')}
    }
  }
`;

function ScheduleSubEvent({
  attire,
  endTime,
  id,
  location,
  name,
  notes,
  time,
  visible,
}: Gds_SubEvent) {
  const { name: venueName, fullAddress } = location || {};

  const params: EventDateTimeParams = {
    time,
    endTime,
  };

  const { time: formattedTime = '', endTime: formattedEndTime = '' } =
    getEventDateTime(params);

  const hasTime = Boolean(formattedTime || formattedEndTime);

  if (!visible) {
    return null;
  }

  return (
    <SubEvent key={id} data-testid={`event-${id} `}>
      {hasTime && (
        <TimeBlock hasTime={hasTime}>
          <StyledTimeRange
            formattedTime={formattedTime}
            formattedEndTime={formattedEndTime}
          />
        </TimeBlock>
      )}

      <Block hasTime={hasTime}>
        <Name>{name}</Name>

        {(venueName || fullAddress) && (
          <Detail>
            <div>{venueName}</div>
            <div>{fullAddress}</div>
          </Detail>
        )}

        {attire && <Detail>Attire: {attire}</Detail>}

        {notes && <Notes>{notes}</Notes>}
      </Block>
    </SubEvent>
  );
}

export default memo(ScheduleSubEvent);
